import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import WizardProgress from '../site-setup-wizard/components/WizardProgress';
import { setupService } from '../../services/setupService';
import { companyService } from '../../services/companyService';
import { supabase } from '../../lib/supabase';
import { stripeService } from '../../services/stripeService';
import { sanitizeError } from '../../utils/sanitizeError';
import {
  MIN_SITES,
  calculateGraduatedPrice,
} from '../../constants/planConfig';

const steps = [
  { id: 1, title: 'Company Details' },
  { id: 2, title: 'Choose Plan' },
  { id: 3, title: 'Create First Site' },
  { id: 4, title: 'Invite Team' },
  { id: 5, title: 'Daily Site Checklist' },
];

const SetupFlowPlanConfirmation = () => {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteCount, setSiteCount] = useState(5);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [checkoutCancelled, setCheckoutCancelled] = useState(false);
  const [checkoutFailed, setCheckoutFailed] = useState(false);

  useEffect(() => {
    // Handle Stripe return params
    const params = new URLSearchParams(window.location.search);
    const checkoutParam = params?.get('checkout');
    if (checkoutParam === 'cancelled') {
      setCheckoutCancelled(true);
      window.history?.replaceState({}, '', '/setup-flow-plan-confirmation');
    } else if (checkoutParam === 'failed') {
      setCheckoutFailed(true);
      window.history?.replaceState({}, '', '/setup-flow-plan-confirmation');
    } else if (checkoutParam === 'success') {
      window.location?.replace('/checkout-success');
      return;
    }

    // Load company ID — try profile first, fall back to getUserCompany
    const bootstrap = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session?.user) {
          const { data: profile } = await supabase
            ?.from('user_profiles')
            ?.select('company_id')
            ?.eq('id', session?.user?.id)
            ?.maybeSingle();
          if (profile?.company_id) {
            setCompanyId(profile?.company_id);
          } else {
            const { data: company } = await companyService?.getUserCompany();
            if (company?.id) setCompanyId(company?.id);
          }
        }
      } catch (_) {}
      setLoading(false);
    };
    bootstrap();
  }, []);

  // Derive live pricing from current inputs — recalculates on every render
  const count = Math.max(parseInt(siteCount, 10) || MIN_SITES, MIN_SITES);
  const pricing = calculateGraduatedPrice(count, billingCycle);

  const handleSiteCountChange = (e) => {
    const raw = e?.target?.value;
    // Allow empty string while typing; clamp on blur/submit
    setSiteCount(raw === '' ? '' : parseInt(raw, 10) || MIN_SITES);
    setSubmitError('');
  };

  const handleSiteCountBlur = () => {
    const parsed = parseInt(siteCount, 10);
    if (!parsed || parsed < MIN_SITES) setSiteCount(MIN_SITES);
  };

  const handleStartTrial = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      if (!companyId) {
        setSubmitError('Could not determine your company. Please refresh the page.');
        setIsSubmitting(false);
        return;
      }

      const finalCount = Math.max(parseInt(siteCount, 10) || MIN_SITES, MIN_SITES);

      console.log('[PlanConfirmation] submitting:', { rawSiteCount: siteCount, finalCount, billingCycle, companyId });

      // Persist selection so the flow can resume if interrupted.
      // Do NOT write plan_tier to DB here — webhook / sync-subscription sets it.
      setupService?.saveProgress(2, { siteCount: finalCount, billingCycle });

      // Write max_active_sites early so the dashboard shows "0 / N" immediately,
      // even before Stripe checkout completes. The webhook will overwrite this
      // with the authoritative value from the subscription quantity.
      try {
        await supabase?.from('companies')?.update({ max_active_sites: finalCount })?.eq('id', companyId);
      } catch (_) {}

      const { data, error: checkoutError } = await stripeService?.createCheckoutSession(
        companyId,
        finalCount,
        billingCycle,
        '/checkout-success'
      );

      if (checkoutError) throw checkoutError;
      if (data?.url) {
        window.location.href = data?.url;
        return;
      }
      throw new Error('No checkout URL returned from Stripe.');
    } catch (err) {
      setSubmitError(sanitizeError(err?.message, 'Failed to start checkout. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Choose Your Plan</h1>
          <p className="text-muted-foreground">Volume discount built in — scale and your per-site rate drops automatically.</p>
        </div>

        <WizardProgress currentStep={2} totalSteps={5} steps={steps} onExit={() => navigate('/')} />

        {/* Checkout cancelled / failed banners */}
        {checkoutCancelled && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">Your payment was not completed.</p>
            <p className="text-sm text-amber-700 dark:text-amber-500 mt-0.5">You can try again below — no charge was made.</p>
          </div>
        )}
        {checkoutFailed && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm font-semibold text-red-800 dark:text-red-400">Payment failed.</p>
            <p className="text-sm text-red-700 dark:text-red-500 mt-0.5">Your card was not charged. Please try again with a different payment method.</p>
          </div>
        )}

        {/* Main pricing card */}
        <div className="bg-card border border-border rounded-2xl shadow-elevation-2 p-6 md:p-8">

          {/* Site count input */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-foreground mb-1">
              How many sites do you manage?
            </label>
            <p className="text-xs text-muted-foreground mb-3">
              Minimum {MIN_SITES} sites. You can adjust this at any time.
            </p>
            <input
              type="number"
              min={MIN_SITES}
              value={siteCount}
              onChange={handleSiteCountChange}
              onBlur={handleSiteCountBlur}
              className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground text-lg font-semibold placeholder-muted-foreground focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/50 transition-colors"
            />
          </div>

          {/* Billing cycle toggle */}
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                billingCycle === 'monthly'
                  ? 'bg-accent text-accent-foreground shadow-md'
                  : 'bg-muted text-muted-foreground hover:text-foreground border border-border'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 relative ${
                billingCycle === 'annual'
                  ? 'bg-accent text-accent-foreground shadow-md'
                  : 'bg-muted text-muted-foreground hover:text-foreground border border-border'
              }`}
            >
              Annual
              <span className={`ml-1.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${
                billingCycle === 'annual'
                  ? 'bg-accent-foreground/20 text-accent-foreground'
                  : 'bg-accent/10 text-accent'
              }`}>
                Save 15%
              </span>
            </button>
          </div>

          {/* Live price display */}
          <div className="bg-muted/50 rounded-xl p-5 mb-6">
            <p className="text-3xl font-bold text-foreground">
              £{pricing.monthlyTotal.toLocaleString('en-GB')}
              <span className="text-base font-normal text-muted-foreground">
                {billingCycle === 'monthly' ? '/month' : '/month eq.'}
              </span>
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {pricing.breakdown
                .map((tier) => `${tier.sites} × £${tier.pricePerSite}`)
                .join(' + ')}
              {' '}={' '}
              <span className="text-foreground font-medium">
                £{pricing.monthlyTotal.toLocaleString('en-GB')}/month{billingCycle === 'annual' ? ' eq.' : ''}
              </span>
            </p>
            {billingCycle === 'annual' && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Billed £{pricing.annualTotal.toLocaleString('en-GB')}/year. Volume discount built in — your per-site rate drops as you scale.
              </p>
            )}
            {billingCycle === 'monthly' && pricing.breakdown.length === 1 && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Volume discount built in — add more sites and your per-site rate drops automatically.
              </p>
            )}
          </div>

          {/* Error */}
          {submitError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{submitError}</p>
            </div>
          )}

          {/* CTA */}
          <button
            onClick={handleStartTrial}
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-4 px-6 rounded-xl text-base transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Redirecting to checkout…
              </>
            ) : (
              <>
                Start your 30-day free trial
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          <p className="text-xs text-muted-foreground text-center mt-3">
            Card required — you won't be charged until your trial ends. Cancel anytime.
          </p>

          <p className="text-xs text-muted-foreground text-center mt-2">
            By subscribing, you agree to our{' '}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground transition-colors">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground transition-colors">Privacy Policy</a>.
          </p>

          <p className="text-xs text-muted-foreground text-center mt-3">
            Prefer to discuss your needs?{' '}
            <a href="mailto:sales@site-proof.app" className="underline hover:text-foreground transition-colors">
              Contact us at sales@site-proof.app
            </a>
          </p>
        </div>

        {/* Trial reassurance */}
        <div className="mt-4 bg-accent/5 border border-accent/20 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
            <p className="text-sm font-semibold text-foreground">What's included in your trial</p>
          </div>
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            <li className="flex items-center gap-2"><span className="text-accent">•</span> 30-day free trial — full access to all features</li>
            <li className="flex items-center gap-2"><span className="text-accent">•</span> No charge if you cancel before the trial ends</li>
            <li className="flex items-center gap-2"><span className="text-accent">•</span> Change your site count at any time</li>
            <li className="flex items-center gap-2"><span className="text-accent">•</span> Switch between monthly and annual billing anytime</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SetupFlowPlanConfirmation;
